import { developerApplicationSchema, ENGINES, MAX_LINKS, PLAN_MAX } from '@mogzo/shared';
import { type FormEvent, type ReactNode, useEffect, useId, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { SiteFooter } from '../../components/SiteFooter';
import { Turnstile } from '../../components/Turnstile';
import { Alert, Button, buttonClass, inputClass, SiteHeader } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { COUNTRIES, sendApplication } from '../../lib/developer-applications';
import { usePublicConfig } from '../../lib/public-config';
import { useSession } from '../../lib/session';

const PERKS = [
  {
    title: 'Game services built in.',
    body: 'Saves, leaderboards, achievements and multiplayer lobbies, with ready-made UI.',
  },
  {
    title: 'A community for your game.',
    body: 'Players talk, share runs and challenge friends next to your game.',
  },
  {
    title: 'Posts people can play.',
    body: 'Challenges and daily puzzles show up in feeds and open your game in place.',
  },
] as const;

const STEPS = [
  'Send this form and confirm your email.',
  'We review every application by hand. We may email you a question.',
  'Approved? You get an email to set up your developer account and create your first project.',
] as const;

type FieldName =
  | 'name'
  | 'email'
  | 'studio'
  | 'country'
  | 'engine'
  | 'links'
  | 'plan'
  | 'acceptTerms';
type Errors = Partial<Record<FieldName, string>>;

const underline =
  'font-semibold text-ink underline decoration-brand decoration-2 underline-offset-4';

/** Label, control and error/hint line, wired up for screen readers. */
function FormRow({
  label,
  optional,
  hint,
  error,
  children,
}: {
  label: string;
  optional?: boolean;
  hint?: ReactNode;
  error?: string;
  children: (props: {
    id: string;
    'aria-describedby'?: string;
    'aria-invalid'?: true;
  }) => ReactNode;
}) {
  const id = useId();
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-semibold text-ink">
        {label} {optional && <span className="font-normal text-muted">(optional)</span>}
      </label>
      {children({ id, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined })}
      {error ? (
        <p id={`${id}-error`} className="text-xs font-semibold text-red-700">
          {error}
        </p>
      ) : (
        hint && (
          <p id={`${id}-hint`} className="text-xs text-muted">
            {hint}
          </p>
        )
      )}
    </div>
  );
}

export function DeveloperApply() {
  const config = usePublicConfig();
  const { me } = useSession();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [studio, setStudio] = useState('');
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [engine, setEngine] = useState('');
  const [links, setLinks] = useState([{ id: 0, value: '' }]);
  const [plan, setPlan] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  const [errors, setErrors] = useState<Errors>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Signed-in players apply with the email of the account they already have.
  useEffect(() => {
    if (!me) return;
    setName((value) => value || me.displayName);
    setEmail((value) => value || me.email);
  }, [me]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    const parsed = developerApplicationSchema.safeParse({
      name,
      email: email.trim(),
      studio: studio.trim() || undefined,
      country,
      engine,
      links: links.map((link) => link.value.trim()).filter(Boolean),
      plan,
      acceptTerms,
    });
    if (!parsed.success) {
      const found: Errors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as FieldName;
        found[field] ??= issue.message;
      }
      setErrors(found);
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      await sendApplication(parsed.data, captcha);
      navigate('/developers/apply/sent', { state: { email: parsed.data.email } });
    } catch (failed) {
      setError(
        failed instanceof ApiError
          ? failed.message
          : 'We couldn’t send that. Try again in a moment.',
      );
      setCaptchaReset((n) => n + 1);
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col">
      <SiteHeader>
        {me !== undefined && !me && (
          <Link to="/sign-in" className={buttonClass('secondary', false)}>
            Sign in
          </Link>
        )}
      </SiteHeader>

      <main className="mx-auto grid w-full max-w-5xl flex-1 items-start gap-10 px-4 py-10 sm:px-6 sm:py-14 lg:grid-cols-[1fr_minmax(0,520px)] lg:gap-12">
        <section className="space-y-7">
          <div>
            <p className="text-xs font-bold tracking-[0.25em] text-muted uppercase">
              Developer partners
            </p>
            <h1 className="mt-3 text-4xl leading-[1.1] font-bold tracking-tight text-ink sm:text-5xl">
              Build browser games on{' '}
              <span className="rounded-lg bg-brand px-1.5 box-decoration-clone">MOGZO</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-muted">
              Bring your game. We give it players, a community and a backend, without you running a
              server.
            </p>
          </div>
          <ul className="space-y-4">
            {PERKS.map((perk) => (
              <li key={perk.title} className="flex gap-3">
                <span aria-hidden className="mt-2 size-2 shrink-0 rounded-full bg-brand" />
                <p className="leading-relaxed text-muted">
                  <strong className="text-ink">{perk.title}</strong> {perk.body}
                </p>
              </li>
            ))}
          </ul>
          <section
            aria-labelledby="how-heading"
            className="rounded-2xl border border-line bg-card p-6"
          >
            <h2 id="how-heading" className="text-base font-bold text-ink">
              How it works
            </h2>
            <ol className="mt-4 space-y-3.5">
              {STEPS.map((step, index) => (
                <li key={step} className="flex gap-3 text-sm leading-relaxed text-ink">
                  <span className="font-mono text-xs font-medium text-muted leading-6">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
          </section>
        </section>

        <section
          aria-labelledby="form-heading"
          className="rounded-3xl border border-line bg-card p-6 shadow-[0_1px_2px_rgba(27,21,47,0.04)] sm:p-8"
        >
          <h2 id="form-heading" className="text-2xl font-bold text-ink">
            Apply
          </h2>
          <p className="mt-2 text-sm text-muted">
            Takes about three minutes. Fields are required unless marked optional.
          </p>

          <form onSubmit={onSubmit} noValidate className="mt-6 space-y-5">
            {error && <Alert>{error}</Alert>}
            <div className="grid gap-4 sm:grid-cols-2">
              <FormRow label="Your name" error={errors.name}>
                {(props) => (
                  <input
                    {...props}
                    className={inputClass}
                    autoComplete="name"
                    maxLength={80}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                  />
                )}
              </FormRow>
              <FormRow label="Studio or team" optional error={errors.studio}>
                {(props) => (
                  <input
                    {...props}
                    className={inputClass}
                    autoComplete="organization"
                    maxLength={80}
                    value={studio}
                    onChange={(event) => setStudio(event.target.value)}
                  />
                )}
              </FormRow>
            </div>
            <FormRow
              label="Email"
              error={errors.email}
              hint="Your developer account uses this email. Already play on MOGZO? Use the same one."
            >
              {(props) => (
                <input
                  {...props}
                  className={inputClass}
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              )}
            </FormRow>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormRow label="Country" error={errors.country}>
                {(props) => (
                  <select
                    {...props}
                    className={inputClass}
                    autoComplete="country"
                    value={country}
                    onChange={(event) => setCountry(event.target.value)}
                  >
                    <option value="">Choose a country</option>
                    {COUNTRIES.map((option) => (
                      <option key={option.code} value={option.code}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                )}
              </FormRow>
              <FormRow label="Engine or tools" error={errors.engine}>
                {(props) => (
                  <select
                    {...props}
                    className={inputClass}
                    value={engine}
                    onChange={(event) => setEngine(event.target.value)}
                  >
                    <option value="">Choose one</option>
                    {ENGINES.map((option) => (
                      <option key={option}>{option}</option>
                    ))}
                  </select>
                )}
              </FormRow>
            </div>

            <fieldset className="space-y-2">
              <legend className="text-sm font-semibold text-ink">
                Links to your games or work
              </legend>
              {links.map((link, index) => (
                <div key={link.id} className="flex gap-2">
                  <input
                    className={inputClass}
                    type="url"
                    inputMode="url"
                    placeholder="https://"
                    aria-label={index === 0 ? 'Link 1' : `Link ${index + 1} (optional)`}
                    aria-invalid={errors.links ? true : undefined}
                    aria-describedby="links-help"
                    maxLength={300}
                    value={link.value}
                    onChange={(event) =>
                      setLinks(
                        links.map((item) =>
                          item.id === link.id ? { ...item, value: event.target.value } : item,
                        ),
                      )
                    }
                  />
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => setLinks(links.filter((item) => item.id !== link.id))}
                      className={buttonClass('secondary', false)}
                      aria-label={`Remove link ${index + 1}`}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <p
                id="links-help"
                className={`text-xs ${errors.links ? 'font-semibold text-red-700' : 'text-muted'}`}
              >
                {errors.links ??
                  `Up to ${MAX_LINKS} links: itch.io, Newgrounds, GitHub, your website or a gameplay video. At least one.`}
              </p>
              {links.length < MAX_LINKS && (
                <button
                  type="button"
                  onClick={() =>
                    setLinks([...links, { id: Math.max(...links.map((l) => l.id)) + 1, value: '' }])
                  }
                  className="text-sm font-semibold text-ink underline decoration-brand decoration-2 underline-offset-4 hover:decoration-ink"
                >
                  Add another link
                </button>
              )}
            </fieldset>

            <FormRow
              label="What do you want to make on MOGZO?"
              error={errors.plan}
              hint="The game you’d bring first, and how players would use it here."
            >
              {(props) => (
                <div>
                  <textarea
                    {...props}
                    className={`${inputClass} resize-y`}
                    rows={5}
                    maxLength={PLAN_MAX}
                    value={plan}
                    onChange={(event) => setPlan(event.target.value)}
                  />
                  <p className="mt-1 text-right font-mono text-xs text-muted" aria-live="polite">
                    {plan.length.toLocaleString('en')} / {PLAN_MAX.toLocaleString('en')}
                  </p>
                </div>
              )}
            </FormRow>

            <div className="space-y-1.5">
              <label className="flex items-start gap-3 text-sm leading-relaxed text-ink">
                <input
                  type="checkbox"
                  className="mt-1 size-4 shrink-0 accent-ink"
                  checked={acceptTerms}
                  aria-invalid={errors.acceptTerms ? true : undefined}
                  onChange={(event) => setAcceptTerms(event.target.checked)}
                />
                <span>
                  I’m 18 or older, I agree to the{' '}
                  <Link to="/developer-terms" target="_blank" className={underline}>
                    Developer Terms
                  </Link>{' '}
                  and I have read the{' '}
                  <Link to="/privacy" target="_blank" className={underline}>
                    Privacy Policy
                  </Link>
                  .
                </span>
              </label>
              {errors.acceptTerms && (
                <p className="text-xs font-semibold text-red-700">{errors.acceptTerms}</p>
              )}
            </div>

            <Turnstile
              siteKey={config?.turnstileSiteKey ?? null}
              onToken={setCaptcha}
              resetKey={captchaReset}
            />
            <Button busy={busy} disabled={!!config?.turnstileSiteKey && !captcha}>
              Send application
            </Button>
            <p className="text-center text-xs text-muted">
              We’ll email you to confirm your address. We only review confirmed applications.
            </p>
          </form>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
